import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { Card, Chip, Spinner, useToast } from '../components/ui';
import type { HouseholdSummary } from '../types';

/**
 * The operator's view (§7).
 *
 * Sign-up is open, so this is how the person running Kitja can see who is on
 * it, how much of it they are using, and remove a household that should not
 * be there. Counts and dates only — what a family is having for dinner is
 * theirs, and there is no route from here into it.
 */
export function AdminPage() {
  const toast = useToast();
  const [households, setHouseholds] = useState<HouseholdSummary[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);

  const load = useCallback(async () => {
    const result = await api.adminHouseholds();
    setHouseholds(result.households);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!households) {
    return (
      <div className="stack">
        <h1 className="page-title">Households</h1>
        <Spinner />
      </div>
    );
  }

  const remove = async (household: HouseholdSummary) => {
    setBusy(true);
    try {
      await api.adminDeleteHousehold(household.id);
      await load();
      toast(`${household.name} removed`);
      setConfirming(null);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not remove that household');
    } finally {
      setBusy(false);
    }
  };

  const totals = households.reduce(
    (sum, h) => ({
      people: sum.people + h.memberCount,
      emails: sum.emails + h.emailsSent,
      weeks: sum.weeks + h.weeksPlanned,
    }),
    { people: 0, emails: 0, weeks: 0 },
  );

  return (
    <div className="stack">
      <h1 className="page-title">Households</h1>
      <p className="dim">
        {plural(households.length, 'household')} · {plural(totals.people, 'person', 'people')} ·{' '}
        {plural(totals.weeks, 'week')} planned · {plural(totals.emails, 'email')} sent
      </p>

      {households.map((household) => (
        <Card
          key={household.id}
          title={
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <h2 style={{ margin: 0 }}>{household.name}</h2>
              {household.isYours && <Chip kind="accent">You</Chip>}
              {!household.setupComplete && <Chip>Never set up</Chip>}
              {isDormant(household) && <Chip kind="warn">Dormant</Chip>}
            </div>
          }
          actions={
            household.isYours ? null : confirming === household.id ? (
              <div className="row" style={{ gap: 6 }}>
                <button
                  className="btn small warn"
                  disabled={busy}
                  onClick={() => void remove(household)}
                >
                  Delete everything
                </button>
                <button className="btn small" disabled={busy} onClick={() => setConfirming(null)}>
                  Keep
                </button>
              </div>
            ) : (
              <button className="btn small" onClick={() => setConfirming(household.id)}>
                Remove
              </button>
            )
          }
        >
          {confirming === household.id && (
            <p className="banner warn">
              This deletes {household.name} entirely — its people, its plans, its shopping and its
              record of what Kitja has emailed on its behalf. It cannot be undone.
            </p>
          )}
          <dl className="admin-grid">
            <Fact label="Owner" value={household.ownerEmail ?? '—'} />
            <Fact label="Signed up" value={formatDate(household.createdAt)} />
            <Fact label="Last active" value={formatRelative(household.lastActive)} />
            <Fact label="People" value={String(household.memberCount)} />
            <Fact label="Sign-ins" value={String(household.userCount)} />
            <Fact label="Weeks planned" value={String(household.weeksPlanned)} />
            <Fact label="Emails sent" value={String(household.emailsSent)} />
            <Fact label="Where" value={household.location} />
          </dl>
        </Card>
      ))}
    </div>
  );
}

function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="dim">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/** Nothing for a month — worth noticing, in either direction. */
function isDormant(household: HouseholdSummary): boolean {
  if (!household.lastActive) return true;
  return Date.now() - Date.parse(household.lastActive) > 30 * 24 * 60 * 60 * 1000;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? 'a month ago' : `${months} months ago`;
}
